import { Observable, forkJoin, from, map, switchMap } from "rxjs";

import { services } from "@progressive-development/pd-spa-helper";
import type { QueryConstraint } from "@progressive-development/pd-provider-interfaces";
import { ris, wahl } from "@progressive-development/politik-model";

import {
  COL_BERATUNGEN,
  COL_MEETINGS,
  COL_ORGANIZATIONS,
  COL_PAPERS,
  COL_PARTEIEN,
} from "../../../config/collections.js";
import { segmentAlsAbfrage } from "../entscheidungen-abfrage.js";

/**
 * Service für Block 2 — Drucksachen, Stationen, Sitzungen, Stammdaten.
 *
 * ZWEI WEGE, nach Art der Daten getrennt, nicht nach Bequemlichkeit:
 *
 * - **Fach-Daten direkt aus der Datenbank.** Sie sind amtliche offene Daten:
 *   öffentlich lesbar, read-only, und sie ändern sich einmal je Pipeline-Lauf.
 *   Ein Function-Aufruf davor leistete nichts — keine Autorisierung (die
 *   Regeln erlauben das Lesen ohnehin), kein Verbergen (die Daten sind
 *   veröffentlicht), kein Zusammensetzen (das macht der Export) — und kostete
 *   Kaltstart und Rechenzeit.
 * - **Die Liste liest hier gar nicht.** Sie hält das Listen-Artefakt im
 *   Speicher (`artefakt-service.ts`); `ladeDrucksachen` bleibt für die
 *   Gebietsseiten, die eine kleine, einfach gefilterte Auswahl brauchen.
 *
 * Alles Benutzergebundene (Profil, Merkliste) läuft unverändert über die
 * Functions — dort zählen Identität, Validierung und Schreibrechte.
 *
 * Die Abfragen halten sich an das, was ein Dokumentspeicher kann: höchstens
 * ein `in` je Abfrage, Sortierfelder müssen indiziert sein, und ein `orderBy`
 * schließt Dokumente ohne dieses Feld aus (s. `ladeBeratungen`).
 */

/** Nachschlage-Daten, die jede Ansicht des Kapitels braucht. */
export interface EntscheidungenStammdaten {
  gremien: ris.Organization[];
  parteien: wahl.Partei[];
}

/** Eine Drucksache mit ihrem Beratungsweg — was die Detailseite zeigt. */
export interface DrucksacheMitWeg {
  paper: ris.Paper;
  beratungen: ris.Beratung[];
}

/** Eine Seite der Liste; der Cursor führt zur nächsten, sein Fehlen ans Ende. */
export interface DrucksachenSeite {
  items: ris.Paper[];
  nextCursor?: string;
  /**
   * Wie viele Drucksachen der Filter insgesamt trifft.
   *
   * Kommt aus einer eigenen Zähl-Abfrage — eine Dokumentdatenbank liefert
   * keine Gesamtzahl neben einer Seite. Nur beim ERSTEN Aufruf gezählt: Beim
   * Nachladen ändert sich der Filter nicht, also auch die Zahl nicht.
   */
  gesamt?: number;
}

export class EntscheidungenService {
  /** Der Datenbank-Zugang ist optional konfiguriert — hier ist er Pflicht. */
  private get _db() {
    const db = services.database;
    if (!db) {
      throw new Error(
        "Kein Datenbank-Provider konfiguriert — die Fach-Daten werden direkt gelesen " +
          "(enableDatabase in config/provider-setup.ts).",
      );
    }
    return db;
  }

  /**
   * Übersetzt den Filter in Abfrage-Bedingungen.
   *
   * ZWEI GRENZEN eines Dokumentspeichers, die hier sichtbar werden:
   *
   * 1. Höchstens EIN `in` je Abfrage. Deshalb wird ein einzeln gewähltes
   *    Instrument als Gleichheit geschickt — das ist frei kombinierbar — und
   *    das `in` bleibt für den Fall reserviert, in dem es wirklich gebraucht
   *    wird.
   * 2. Suchtreffer kommen als Referenzen und brauchen ebenfalls ein `in`.
   *    Mehrere Instrumente UND eine Suche gleichzeitig sind deshalb nicht in
   *    einer Abfrage ausdrückbar; in dem Fall gewinnt die Suche, und das
   *    Instrument wird als Gleichheit nachgezogen, sobald es eindeutig ist.
   *    Der verbleibende Fall ist als Anforderung notiert.
   */
  private _bedingungen(filter: ris.PaperListFilter): QueryConstraint[] {
    const bedingungen: QueryConstraint[] = [];
    const instrumente = filter.instrument ?? [];
    const referenzen = filter.referenzen;

    if (filter.bezirk) {
      // Trifft die Vereinigung aus Ortsbezug, Federführung und Stationen.
      bedingungen.push({
        type: "where",
        field: "derived.bezirke",
        operator: "array-contains",
        value: filter.bezirk,
      });
    }

    if (referenzen) {
      // Firestore begrenzt `in` auf 30 Werte — die Rangfolge liefert die
      // stärksten Treffer zuerst, der Rest fällt bewusst weg.
      bedingungen.push({
        type: "where",
        field: "reference",
        operator: "in",
        value: referenzen.slice(0, 30),
      });
    }

    if (instrumente.length === 1) {
      bedingungen.push({
        type: "where",
        field: "derived.instrument",
        operator: "==",
        value: instrumente[0],
      });
    } else if (instrumente.length > 1 && !referenzen) {
      bedingungen.push({
        type: "where",
        field: "derived.instrument",
        operator: "in",
        value: instrumente,
      });
    }

    const gleichheiten: [string, unknown][] = [
      ["derived.urheberArt", filter.urheberArt],
      ["derived.urheberFraktion", filter.urheberFraktion],
      ["derived.zustaendigeEbene", filter.zustaendigeEbene],
      // Ortsangabe der Quelle; s. PaperListFilter.stadtteil (kein Umkehrschluss).
      ["derived.stadtteil", filter.stadtteil],
      ["derived.jahr", filter.jahr],
      ["letzteStation.ergebnisKlasse", filter.ergebnisKlasse],
    ];
    for (const [feld, wert] of gleichheiten) {
      if (wert !== undefined && wert !== null && wert !== "") {
        bedingungen.push({
          type: "where",
          field: feld,
          operator: "==",
          value: wert as string | number,
        });
      }
    }

    return bedingungen;
  }

  private _sortierung(sortierung: ris.PaperListFilter["sortierung"]) {
    if (sortierung === "referenz") return [{ field: "reference", direction: "asc" as const }];
    if (sortierung === "aelteste") return [{ field: "date", direction: "asc" as const }];
    return [{ field: "date", direction: "desc" as const }];
  }

  /**
   * Eine Seite der Drucksachen-Liste.
   *
   * Ohne Cursor ist es die erste Seite — dann wird zusätzlich gezählt, damit
   * die Oberfläche eine ehrliche Trefferzahl nennen kann statt „mehr als N".
   * Beim Nachladen entfällt das: Derselbe Filter trifft dieselbe Zahl.
   *
   * INDIZES: `firestore-politik.indexes.json` trägt für `papers` nur noch
   * die drei Kombinationen der Gebiets- und BV-Seiten (`derived.bezirke`,
   * `derived.stadtteil`, `letzteStation.gremiumId` — je mit `date` desc).
   * Jede andere Filter-/Sortier-Kombination, die dieser Aufbau noch zulässt,
   * scheitert live an einem fehlenden Index; die Liste /entscheidungen
   * rechnet im Browser über das Artefakt.
   */
  public ladeDrucksachen(filter: ris.PaperListFilter): Observable<DrucksachenSeite> {
    const bedingungen = this._bedingungen(filter);
    const seite$ = from(
      this._db.getCollection<ris.Paper>(COL_PAPERS, {
        constraints: bedingungen,
        orderBy: this._sortierung(filter.sortierung),
        limit: filter.limit ?? 20,
        startAfter: filter.cursor,
      }),
    ).pipe(
      map((seite) => ({
        items: seite.documents.map((d) => d.data),
        nextCursor: seite.nextCursor,
      })),
    );

    if (filter.cursor) return seite$;

    return forkJoin({
      seite: seite$,
      gesamt: from(this._db.getCollectionCount(COL_PAPERS, { constraints: bedingungen })),
    }).pipe(map(({ seite, gesamt }) => ({ ...seite, gesamt })));
  }

  /**
   * Die Stationen einer Drucksache.
   *
   * BEWUSST OHNE `orderBy`: Ein Sortierfeld schließt Dokumente aus, die es
   * nicht haben — und Stationen ohne Sitzung im Bestand haben kein Datum.
   * Sie würden lautlos verschwinden, obwohl die Detailseite sie ausdrücklich
   * zeigen muss. Die Reihenfolge stellt der Beratungsweg selbst her.
   */
  public ladeBeratungen(paperId: string): Observable<ris.Beratung[]> {
    return from(
      this._db.getCollection<ris.Beratung>(COL_BERATUNGEN, {
        constraints: [{ type: "where", field: "paperId", operator: "==", value: paperId }],
      }),
    ).pipe(map((seite) => seite.documents.map((d) => d.data)));
  }

  /**
   * Eine Drucksache samt Beratungsweg.
   *
   * Adressiert wird über das Segment der URL — den sprechenden Slug, solange
   * die Pipeline ihn liefert, sonst die amtliche Referenz im Wortlaut. Die
   * Dokument-ID ist ein Hash, deshalb in beiden Fällen eine Abfrage statt
   * eines direkten Zugriffs. Läge der Slug als Dokument-ID vor, wäre es ein
   * einzelner Lesezugriff — offene Frage an die Pipeline.
   */
  public ladeDrucksache(segment: string): Observable<DrucksacheMitWeg> {
    const { feld, wert } = segmentAlsAbfrage(segment);
    return from(
      this._db.getCollection<ris.Paper>(COL_PAPERS, {
        constraints: [{ type: "where", field: feld, operator: "==", value: wert }],
        limit: 1,
      }),
    ).pipe(
      switchMap((seite) => {
        const paper = seite.documents[0]?.data;
        if (!paper) throw new Error(`ris.Paper ${wert} nicht gefunden`);
        return this.ladeBeratungen(paper.id).pipe(map((beratungen) => ({ paper, beratungen })));
      }),
    );
  }

  /** Die Stationen EINER Sitzung — die Tagesordnung nennt keine Drucksachen. */
  public ladeStationenDerSitzung(meetingId: string): Observable<ris.Beratung[]> {
    return from(
      this._db.getCollection<ris.Beratung>(COL_BERATUNGEN, {
        constraints: [{ type: "where", field: "meetingId", operator: "==", value: meetingId }],
      }),
    ).pipe(map((seite) => seite.documents.map((d) => d.data)));
  }

  /** Eine Sitzung samt der Stationen, die auf ihrer Tagesordnung standen. */
  public ladeSitzung(id: string): Observable<{ sitzung: ris.Meeting; stationen: ris.Beratung[] }> {
    return forkJoin({
      // Die Sitzung wird über ihre Id adressiert — ein einzelner Lesezugriff.
      sitzung: from(this._db.getDocument<ris.Meeting>(COL_MEETINGS, id)).pipe(
        map((snapshot) => {
          if (!snapshot.exists) throw new Error(`Sitzung ${id} nicht gefunden`);
          return snapshot.data;
        }),
      ),
      stationen: this.ladeStationenDerSitzung(id),
    });
  }

  /**
   * Gremien und Parteien — klein, vollständig, überall gebraucht.
   *
   */
  public ladeStammdaten(): Observable<EntscheidungenStammdaten> {
    return forkJoin({
      gremien: from(this._db.getCollection<ris.Organization>(COL_ORGANIZATIONS)).pipe(
        map((seite) => seite.documents.map((d) => d.data)),
      ),
      parteien: from(this._db.getCollection<wahl.Partei>(COL_PARTEIEN)).pipe(
        map((seite) => seite.documents.map((d) => d.data)),
      ),
    });
  }
}

export const entscheidungenService = new EntscheidungenService();
